import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { insertItem, patch } from '@ngxs/store/operators';
import { ERequestType, Flags, ReferralStatus, RequestState } from '@shs-digi-care-monorepo/interfaces';
import {
    catchError,
    concatMap,
    defaultIfEmpty,
    delay,
    filter,
    finalize,
    forkJoin,
    map,
    Observable,
    of,
    repeat,
    switchMap,
    take,
    tap,
    throwError
} from 'rxjs';
import { UserService } from '../../../common/auth/services/user.service';
import { ICommonRequest } from '../../../common/shared/interfaces/common-request.interface';
import { ICreateComment } from '../../../common/shared/interfaces/ICreateComment';
import { IFile } from '../../../common/shared/interfaces/IFile';
import { ILegacyPaginatedComments } from '../../../common/shared/interfaces/ILegacyPaginatedComments';
import { PreRequest, Request } from '../../../common/shared/interfaces/IRequest';
import { IRequestComment } from '../../../common/shared/interfaces/IRequestComment';
import { IUser } from '../../../common/shared/interfaces/iUser';
import { StateAttachment } from '../../../common/shared/interfaces/stateAttachment';
import { ActivityStreamService } from '../../../common/shared/services/activity.service';
import { NamCommentService } from '../../../common/shared/services/comment.service';
import { SnackbarService } from '../../../common/shared/services/snackbar.service';
import { MDLA_SCOPE, MDLAScope } from '../../../common/shared/tokens/mdla-scope.token';
import { PartialComment } from '../../../common/shared/types/PartialComment';
import { hasOpenReferralForUser } from '../../../common/shared/utils/referral.util';
import { isInternal, isOnlyInternalOrExternalRequestor } from '../../../common/shared/utils/user-role.util';
import { Requests, RequestsState } from '../../../common/store/request';
import { requestStateConfig } from '../../../common/store/request/request-state.config';
import { getRequestInformation, RequestsStateModel } from '../../../common/store/request/request.state';
import { Users, UsersState } from '../../../common/store/user';
import { NamAttachmentService } from '../../services/attachment.service';
import { NamRequestService } from '../../services/request.service';
import { NamRequestOperations } from './operations/request.operations';
import { NamRequests } from './request.actions';
import { RequestPollingCriteria } from './interfaces/request-polling-criteria.interface';
import { RequestPollingField } from './types/request-polling-field.type';

type ParallelReferralTransitionResponse = Record<string, unknown> | { comments: IRequestComment[] };

@State({
    ...requestStateConfig,
    name: 'namRequests'
})
@Injectable()
export class NamRequestsState extends RequestsState<Request, Request> {
    protected override requestService: NamRequestService;
    protected override attachmentService: NamAttachmentService;
    protected override commentService: NamCommentService;
    protected override activityStreamService: ActivityStreamService;
    protected override userService: UserService;
    protected override store: Store;
    protected override stateScope: MDLAScope;
    private snackbarService: SnackbarService;
    private readonly pollingFieldChangeCheckers: Partial<
        Record<
            RequestPollingField,
            (requestSnapshot: Request | undefined, updated: Request, currentStatus: string | undefined) => boolean
        >
    > = {
        status: (requestSnapshot, updated, currentStatus) => {
            return updated.status !== (requestSnapshot?.status ?? currentStatus);
        },
        referrals: (requestSnapshot, updated) => {
            if (!requestSnapshot) {
                return true;
            }

            return this.hasCustomRequestUpdate(requestSnapshot, updated);
        }
    };

    constructor() {
        const requestService = inject(NamRequestService);
        const attachmentService = inject(NamAttachmentService);
        const commentService = inject(NamCommentService);
        const activityStreamService = inject(ActivityStreamService);
        const userService = inject(UserService);
        const store = inject(Store);
        const stateScope = inject<MDLAScope>(MDLA_SCOPE);
        const snackbarService = inject(SnackbarService);

        super(requestService, attachmentService, commentService, activityStreamService, userService, store, stateScope);

        this.requestService = requestService;
        this.attachmentService = attachmentService;
        this.commentService = commentService;
        this.activityStreamService = activityStreamService;
        this.userService = userService;
        this.store = store;
        this.stateScope = stateScope;
        this.snackbarService = snackbarService;
    }

    @Action(NamRequests.create, { cancelUncompleted: true })
    public createRequest(
        context: StateContext<RequestsStateModel>,
        { payload, comment }: NamRequests.create
    ): Observable<Request> {
        context.patchState({ loading: true });
        return this.createRequestCommon(context, () => this.requestService.createRequest(payload), comment).pipe(
            finalize(() => {
                setTimeout(() => {
                    this.store.dispatch(new Users.loadMe({ invalidate: true })); // Update user info after creating a request to update default SHS-Team
                }, 500);
            })
        );
    }

    @Action(NamRequests.update, { cancelUncompleted: true })
    public updateRequest(
        context: StateContext<RequestsStateModel>,
        { id, updates, finishLoading }: NamRequests.update
    ): Observable<Request> {
        context.patchState({ loading: true });
        return this.updateRequestCommon(
            context,
            () => this.requestService.updateRequest(id, updates),
            id,
            finishLoading
        );
    }

    @Action(NamRequests.closeRequest)
    public closeRequest(
        context: StateContext<RequestsStateModel>,
        { comment, classification, isSelfServiceOpportunity }: NamRequests.closeRequest
    ): Observable<void> {
        const { selectedRequest } = context.getState();
        const requestInfo = getRequestInformation<PreRequest>(selectedRequest, this.stateScope);

        const id = requestInfo?.id;
        const status = requestInfo?.status;

        if (!id) {
            return throwError(() => new Error('No request ID found for closeRequest action.'));
        }

        // NAM classification handling - update request with classification and isSelfServiceOpportunity if provided
        const updateFn = (): Observable<void> => {
            const updates: Partial<Request> = {};
            if (classification) {
                updates.classification = classification;
            }
            if (isSelfServiceOpportunity !== undefined) {
                updates.isSelfServiceOpportunity = isSelfServiceOpportunity;
            }
            if (Object.keys(updates).length > 0) {
                return this.updateRequestCommon(
                    context,
                    () => this.requestService.updateRequest(id, updates),
                    id,
                    false
                ).pipe(map(() => void 0));
            }
            return of(void 0);
        };
        return this.closeRequestCommon(context, comment, id, status || '', updateFn);
    }

    @Action(NamRequests.closeReferralsAndSetRequestUnderReview)
    public closeReferralsAndSetRequestUnderReview(
        context: StateContext<RequestsStateModel>
    ): Observable<void> | undefined {
        const { selectedRequest } = context.getState();
        const requestInfo = getRequestInformation<PreRequest>(selectedRequest, this.stateScope);

        const id = requestInfo?.id;
        const status = requestInfo?.status;

        if (!id) {
            return throwError(
                () => new Error('No request ID found for closeReferralsAndSetRequestUnderReview action.')
            );
        }

        const transition = this.requestService.transitionRequest(id, status as RequestState, RequestState.UNDER_REVIEW);
        const pollForRequestUpdates = this.pollForRequestUpdates(id, context, status, {
            waitForChange: ['status', 'referrals']
        });

        context.patchState({ loading: true });
        return transition.pipe(
            concatMap(() => pollForRequestUpdates),
            concatMap(() => context.dispatch(new Requests.getTransitionChoices(this.stateScope, id))),
            concatMap(() => context.dispatch(new Requests.loadRequestActivityStream(this.stateScope, id))),
            finalize(() => {
                context.patchState({ loading: false });
            })
        );
    }

    @Action(NamRequests.referOutRequest)
    public parallelReferOutRequest(
        context: StateContext<RequestsStateModel>,
        { payload, classificationUpdate }: NamRequests.referOutRequest
    ): Observable<void> {
        const { selectedRequest } = context.getState();
        const requestInfo = getRequestInformation<ICommonRequest>(selectedRequest, this.stateScope);

        const id = requestInfo?.id;
        const status = requestInfo?.status;

        if (!id) {
            return throwError(() => new Error('No request ID found for referOutRequest action.'));
        }

        context.patchState({ loading: true });

        const mappedPayload = NamRequestOperations.mapReferralPayload(payload);

        const transition: Observable<ParallelReferralTransitionResponse> = this.requestService.parallelReferralRequest(
            id,
            status as RequestState,
            mappedPayload,
            classificationUpdate
                ? {
                      ...(classificationUpdate.classification && {
                          classification: classificationUpdate.classification
                      }),
                      ...(classificationUpdate.requestType && {
                          type: classificationUpdate.requestType as unknown as ERequestType
                      })
                  }
                : undefined
        );

        return transition.pipe(
            concatMap((response) => this.handleReferralComments(context, response, payload, requestInfo.id)),
            concatMap(() => this.fetchUpdatedRequestAfterReferral(context, id, status)),
            map(() => void 0),
            finalize(() => {
                context.patchState({ loading: false });
            })
        );
    }

    private handleReferralComments(
        context: StateContext<RequestsStateModel>,
        response: ParallelReferralTransitionResponse,
        payload: NamRequests.referOutRequest['payload'],
        _requestId: string
    ): Observable<unknown> {
        const maybeComments = (response as { comments?: unknown }).comments;
        const comments: IRequestComment[] = Array.isArray(maybeComments) ? (maybeComments as IRequestComment[]) : [];

        if (comments.length === 0) {
            return of(void 0);
        }

        // Comments are already committed when created, so we only need to handle attachment uploads
        const attachmentUploadFn = this.attachmentService.uploadFile.bind(this.attachmentService);
        const attachmentUploads = NamRequestOperations.generateReferralCommentAttachmentUploads(
            comments,
            payload,
            attachmentUploadFn
        );

        if (attachmentUploads.length === 0) {
            return of(void 0);
        }

        return forkJoin(attachmentUploads).pipe(
            catchError((error) => {
                context.patchState({ loading: false });
                console.error('Error while uploading referral comment attachments.', error);
                this.snackbarService.openSnackbar({
                    type: 'Error',
                    title: 'Referral Comments',
                    desc: 'Error while uploading referral comment attachments.'
                });
                throw error;
            })
        );
    }

    private fetchUpdatedRequestAfterReferral(
        context: StateContext<RequestsStateModel>,
        id: string,
        status: string | undefined
    ): Observable<unknown> {
        const fetchUpdated = this.requestService.getRequestById(id).pipe(
            repeat({ delay: 500 }),
            filter((updated) => updated.status !== status),
            take(1),
            tap((updated) => {
                context.patchState({
                    selectedRequest: {
                        ...context.getState().selectedRequest,
                        requestInformation: updated,
                        request: updated
                    }
                });
            })
        );

        const fetchNewTransitionChoices = context.dispatch(new Requests.getTransitionChoices(this.stateScope, id));
        const fetchActivityStream = context.dispatch(new Requests.loadRequestActivityStream(this.stateScope, id));

        return fetchUpdated.pipe(
            concatMap(() =>
                this.updateCommentsState(context, id).pipe(
                    catchError((error) => {
                        this.snackbarService.openSnackbar({
                            type: 'Error',
                            title: 'Comments',
                            desc: 'Failed to load comments.',
                            error
                        });
                        return of(undefined);
                    })
                )
            ),
            concatMap(() => fetchNewTransitionChoices),
            concatMap(() => fetchActivityStream)
        );
    }

    private updateCommentsState(
        context: StateContext<RequestsStateModel>,
        id: string
    ): Observable<ILegacyPaginatedComments> {
        const currentPageSize = context.getState().selectedRequest.comments?.pageSize || 10;
        const me = this.getCurrentUser();

        const reloadComments = this.getCommentsPage(context, id, {
            page: 0,
            cursor: undefined,
            pageSize: currentPageSize
        }).pipe(
            switchMap((paginatedComments) => this.mapAttachmentsToComments(paginatedComments, id, me)),
            tap((paginatedComments) => {
                if (!this.isCurrentSelectedRequest(context, id)) {
                    return;
                }

                context.setState(
                    patch({
                        selectedRequest: patch({
                            comments: {
                                page: paginatedComments.page,
                                pageSize: paginatedComments.pageSize,
                                total: paginatedComments.total,
                                cursor: paginatedComments.cursor,
                                data: paginatedComments.data
                            },
                            loadMoreComments: !!paginatedComments.cursor
                        })
                    })
                );
            })
        );
        return reloadComments;
    }

    protected override getCommentsPage(
        context: StateContext<RequestsStateModel>,
        id: string,
        params: { page?: number; pageSize?: number; cursor?: string }
    ): Observable<ILegacyPaginatedComments> {
        const requestInfo = getRequestInformation<PreRequest>(context.getState().selectedRequest, this.stateScope);

        return this.getCurrentUser().pipe(
            take(1),
            switchMap((user) =>
                this.commentService.getComments(id, params, {
                    referralIds: this.getReadableReferralIds(requestInfo?.referrals ?? [], user)
                })
            )
        );
    }

    private getReadableReferralIds(referrals: PreRequest['referrals'], user: IUser): string[] {
        if (isInternal(user)) {
            return (referrals ?? []).map(({ id: referralId }) => referralId);
        }

        const userEmail = user.email?.trim().toLowerCase();
        const userTeamIds = new Set((user.teams ?? []).map(({ id }) => id));

        return (referrals ?? [])
            .filter(
                (referral) =>
                    referral.referredOutEmail?.trim().toLowerCase() === userEmail ||
                    (!!referral.referredOutTeam && userTeamIds.has(referral.referredOutTeam))
            )
            .map(({ id: referralId }) => referralId);
    }

    private getCurrentUser(): Observable<IUser> {
        return this.store.selectOnce(UsersState.getMe()).pipe(
            switchMap((user) => {
                if (user) {
                    return of(user);
                }
                // User not in store, fetch from service
                return this.userService.getMe().pipe(
                    filter((fetchedUser): fetchedUser is IUser => !!fetchedUser),
                    tap((fetchedUser) => {
                        this.store.dispatch(new Users.setMe({ me: fetchedUser }));
                    }),
                    take(1)
                );
            }),
            filter((user): user is IUser => !!user)
        );
    }

    @Action(NamRequests.sendRequestBackToCril)
    public sendRequestBackToCril(
        context: StateContext<RequestsStateModel>,
        { comment }: NamRequests.sendRequestBackToCril
    ): Observable<void> | undefined {
        const { selectedRequest } = context.getState();
        const requestInfo = getRequestInformation<ICommonRequest>(selectedRequest, this.stateScope);

        const requestId = requestInfo?.id;
        const currentRequestStatus = requestInfo?.status;

        if (!requestId) {
            return throwError(() => new Error('No request ID found for sendRequestBackToCril action.'));
        }
        const currentUser = this.store.selectSnapshot(UsersState.getMe());
        const mustOwnReferral = currentRequestStatus === RequestState.REFERRED_OUT;
        if (mustOwnReferral && !hasOpenReferralForUser(requestInfo, currentUser)) {
            return throwError(
                () => new Error('Current user has no open referral assigned for sendRequestBackToCril action.')
            );
        }

        const { teams: userTeams, email: userEmail } = currentUser || {};

        const referralId = NamRequestOperations.findReferralIdByUserEmailAndTeams(requestInfo, userEmail, userTeams);

        let transitionFn: (() => Observable<void>) | undefined;
        if (currentRequestStatus === RequestState.REFERRED_OUT && referralId) {
            transitionFn = () =>
                this.requestService.transitionWithOpenReferral(requestId, referralId, RequestState.UNDER_REVIEW);
        }

        /*
         * When this is the last open referral, closing it will trigger a request status change to UNDER_REVIEW.
         * We must poll for both the referral and status changes to avoid stopping prematurely on the referral
         * update alone, before the status transition has propagated.
         * If there are multiple open referrals, closing one leaves the request in REFERRED_OUT, so we only
         * need to poll until the referral change is reflected.
         */
        const openReferralCount =
            (requestInfo as PreRequest | undefined)?.referrals?.filter(
                (referral) => referral.status === ReferralStatus.OPEN
            ).length ?? 0;

        const pollingCriteria: RequestPollingCriteria | undefined =
            openReferralCount === 1
                ? {
                      waitForChange: ['status', 'referrals']
                  }
                : undefined;

        return this.handleRequestTransition(
            context,
            comment,
            requestId,
            currentRequestStatus,
            RequestState.UNDER_REVIEW,
            transitionFn,
            pollingCriteria
        );
    }

    @Action(NamRequests.closeReferral)
    public closeReferral(
        context: StateContext<RequestsStateModel>,
        { referralId }: NamRequests.closeReferral
    ): Observable<void> | undefined {
        const { selectedRequest } = context.getState();
        const requestInfo = getRequestInformation<ICommonRequest>(selectedRequest, this.stateScope);

        const requestId = requestInfo?.id;
        const currentRequestStatus = requestInfo?.status;

        if (!requestId) {
            return throwError(() => new Error('No request ID found for closeReferral action.'));
        }

        if (!referralId) {
            return throwError(() => new Error('No referral ID found for closeReferral action.'));
        }

        const hasOpenReferralWithId =
            (requestInfo as PreRequest | undefined)?.referrals?.some(
                (referral) => referral.id === referralId && referral.status === ReferralStatus.OPEN
            ) ?? false;

        if (!hasOpenReferralWithId) {
            return throwError(() => new Error('No open referral found for the provided referral ID.'));
        }

        /*
         * When this is the last open referral, closing it will trigger a request status change to UNDER_REVIEW.
         * We must poll for both the referral and status changes to avoid stopping prematurely on the referral
         * update alone, before the status transition has propagated.
         * If there are multiple open referrals, closing one leaves the request in REFERRED_OUT, so we only
         * need to poll until the referral change is reflected.
         */
        const openReferralCount =
            (requestInfo as PreRequest | undefined)?.referrals?.filter(
                (referral) => referral.status === ReferralStatus.OPEN
            ).length ?? 0;

        const pollingCriteria: RequestPollingCriteria | undefined =
            openReferralCount === 1
                ? {
                      waitForChange: ['status', 'referrals']
                  }
                : undefined;

        return this.executeTransitionAndReload(
            context,
            requestId,
            currentRequestStatus,
            () => this.requestService.transitionWithOpenReferral(requestId, referralId),
            pollingCriteria
        );
    }

    @Action(NamRequests.reassignReferral)
    public reassignReferral(
        context: StateContext<RequestsStateModel>,
        { referralId, payload }: NamRequests.reassignReferral
    ): Observable<void> | undefined {
        const { selectedRequest } = context.getState();
        const requestInfo = getRequestInformation<ICommonRequest>(selectedRequest, this.stateScope);

        const requestId = requestInfo?.id;
        const currentRequestStatus = requestInfo?.status;

        if (!requestId) {
            return throwError(() => new Error('No request ID found for reassignReferral action.'));
        }

        if (!referralId) {
            return throwError(() => new Error('No referral ID found for reassignReferral action.'));
        }

        if (!payload.referredOutEmail && !payload.referredOutTeam) {
            return throwError(
                () => new Error('Neither referredOutEmail nor referredOutTeam provided for reassignReferral action.')
            );
        }

        if (payload.referredOutEmail && payload.referredOutTeam) {
            return throwError(
                () => new Error('Both referredOutEmail and referredOutTeam provided for reassignReferral action.')
            );
        }

        const hasOpenReferralWithId =
            (requestInfo as PreRequest | undefined)?.referrals?.some(
                (referral) => referral.id === referralId && referral.status === ReferralStatus.OPEN
            ) ?? false;

        if (!hasOpenReferralWithId) {
            return throwError(() => new Error('No open referral found for the provided referral ID.'));
        }

        return this.executeTransitionAndReload(context, requestId, currentRequestStatus, () =>
            this.requestService.reassignReferral(requestId, referralId, payload)
        );
    }

    @Action(NamRequests.setQualityFlag, { cancelUncompleted: true })
    public setQualityFlag(
        context: StateContext<RequestsStateModel>,
        { id }: NamRequests.setQualityFlag
    ): Observable<void> {
        return this.requestService.setQualityFlag(id).pipe(
            tap((request) => {
                context.setState(
                    patch({
                        selectedRequest: patch({
                            request: patch({ ...request, flags: insertItem(Flags.IS_QUALITY) }),
                            requestInformation: patch({ ...request, flags: insertItem(Flags.IS_QUALITY) })
                        })
                    })
                );
            }),
            concatMap(() => context.dispatch(new Requests.loadRequestActivityStream(this.stateScope, id)))
        );
    }

    protected override handleRequestTransition(
        ctx: StateContext<RequestsStateModel>,
        comment: PartialComment,
        id: string,
        status: string | undefined,
        nextStatus: RequestState,
        transitionFn?: () => Observable<void>,
        pollingCriteria?: RequestPollingCriteria,
        classificationUpdate?: { classification?: string; requestType?: string }
    ): Observable<void> | undefined {
        ctx.patchState({
            loading: true
        });
        const me = this.store.selectSnapshot(UsersState.getMe());
        const requestor = ctx.getState().selectedRequest.request?.gid;

        const createComment = ctx.dispatch(
            new Requests.createComment(this.stateScope, {
                ...comment,
                // Type-casting is required unfortunately as PartialComment can not be changed without greater effort
                internal: (comment as ICreateComment).internal ?? false,
                requestId: id
            })
        );

        const resolvedTransitionFn =
            transitionFn !== undefined
                ? transitionFn
                : () =>
                      this.requestService.transitionRequest(id, status as RequestState, nextStatus, {
                          ...(classificationUpdate?.classification && {
                              classification: classificationUpdate.classification
                          }),
                          ...(classificationUpdate?.requestType && {
                              type: classificationUpdate.requestType as unknown as ERequestType
                          })
                      });

        /*
         * If the user is a Internal/External Requestor and they are not the creator of the request, we do not poll for request updates,
         * this is because we will forward the user to the menu, because after transitioning the request they should not have access to the request details anymore,
         * and polling would cause a forbidden error
         */
        const shouldSkipPolling = isOnlyInternalOrExternalRequestor(me) && me?.gid !== requestor;
        let _fetchUpdated: Observable<Request | undefined> = this.pollForRequestUpdates(id, ctx, status);
        if (shouldSkipPolling) {
            _fetchUpdated = of(void 0);
        }

        ctx.dispatch(new Requests.getTransitionChoices(this.stateScope, id));

        return createComment.pipe(
            take(1),
            concatMap(() =>
                this.executeTransitionAndReload(
                    ctx,
                    id,
                    status,
                    resolvedTransitionFn,
                    pollingCriteria,
                    shouldSkipPolling
                )
            ),
            finalize(() => {
                ctx.patchState({ loading: false });
            })
        );
    }

    private executeTransitionAndReload(
        ctx: StateContext<RequestsStateModel>,
        id: string,
        status: string | undefined,
        transitionFn: () => Observable<void>,
        pollingCriteria?: RequestPollingCriteria,
        skipPolling = false
    ): Observable<void> {
        ctx.patchState({ loading: true });

        const fetchUpdated: Observable<Request | undefined> = skipPolling
            ? of(void 0)
            : this.pollForRequestUpdates(id, ctx, status, pollingCriteria);

        return transitionFn().pipe(
            concatMap(() => fetchUpdated),
            concatMap(() => ctx.dispatch(new Requests.getTransitionChoices(this.stateScope, id))),
            concatMap(() => ctx.dispatch(new Requests.loadRequestActivityStream(this.stateScope, id))),
            finalize(() => {
                ctx.patchState({ loading: false });
            })
        );
    }

    private pollForRequestUpdates(
        id: string,
        ctx: StateContext<RequestsStateModel>,
        currentStatus: string | undefined,
        criteria?: RequestPollingCriteria
    ): Observable<Request> {
        const requestSnapshot = ctx.getState().selectedRequest.requestInformation as Request | undefined;

        return this.requestService.getRequestById(id).pipe(
            catchError((error) => {
                ctx.patchState({ loading: false });
                return throwError(() => error);
            }),
            repeat({
                count: 40,
                delay: 500
            }),
            filter((updated) => this.matchesPollingCriteria(requestSnapshot, updated, currentStatus, criteria)),
            take(1),
            defaultIfEmpty(requestSnapshot),
            filter((updated): updated is Request => !!updated),
            tap((updated) => {
                ctx.patchState({
                    selectedRequest: {
                        ...ctx.getState().selectedRequest,
                        requestInformation: updated,
                        request: updated
                    },
                    loading: false
                });
            })
        );
    }

    private matchesPollingCriteria(
        requestSnapshot: Request | undefined,
        updated: Request,
        currentStatus: string | undefined,
        criteria?: RequestPollingCriteria
    ): boolean {
        if (!this.hasRequestUpdated(requestSnapshot, updated)) {
            return false;
        }

        const fieldsToCheck = criteria?.waitForChange;

        if (!fieldsToCheck || fieldsToCheck.length === 0) {
            return true;
        }

        return fieldsToCheck.every((field) =>
            this.hasPollingFieldChanged(field, requestSnapshot, updated, currentStatus)
        );
    }

    private hasPollingFieldChanged(
        field: RequestPollingField,
        requestSnapshot: Request | undefined,
        updated: Request,
        currentStatus: string | undefined
    ): boolean {
        const fieldChecker = this.pollingFieldChangeCheckers[field];
        if (fieldChecker) {
            return fieldChecker(requestSnapshot, updated, currentStatus);
        }

        if (!requestSnapshot) {
            return true;
        }

        return updated[field] !== requestSnapshot[field];
    }

    protected override hasCustomRequestUpdate(previous: ICommonRequest, updated: ICommonRequest): boolean {
        const previousReferrals = (previous as PreRequest).referrals || [];
        const newReferrals = (updated as PreRequest).referrals || [];

        if (previousReferrals.length !== newReferrals.length) {
            return true;
        }

        if (previousReferrals.length === 0) {
            return false;
        }

        const previousReferralSignatures = new Map(
            previousReferrals.map((referral) => [referral.id, this.referralSignature(referral)])
        );

        for (const referral of newReferrals) {
            const previousSignature = previousReferralSignatures.get(referral.id);
            if (previousSignature === undefined) {
                return true;
            }

            if (previousSignature !== this.referralSignature(referral)) {
                return true;
            }
        }

        return false;
    }

    private referralSignature(referral: NonNullable<PreRequest['referrals']>[number]): string {
        return `${referral.status}|${referral.referredOutEmail ?? ''}|${referral.referredOutTeam ?? ''}|${referral.updatedAt ?? ''}`;
    }

    protected override mapAttachmentsToComments(
        paginatedComments: ILegacyPaginatedComments,
        id: string,
        me: Observable<IUser>
    ): Observable<ILegacyPaginatedComments> {
        // With the new comment-service, files are returned directly in the comment response
        // We need to convert IFile[] to StateAttachment[] for backward compatibility
        return me.pipe(
            map((currentUser) => {
                paginatedComments.data.forEach((comment) => {
                    if (comment.files && comment.files.length > 0) {
                        const files = comment.files.map((file) => {
                            const fileValue = file as IFile | string;
                            return typeof fileValue === 'string' ? { name: fileValue } : fileValue;
                        });
                        comment.files = files;

                        // Map IFile[] to StateAttachment[] format
                        comment.attachments = files.map((file) => ({
                            Key: file.name,
                            name: file.name,
                            LastModified: file.deletedAt ? String(file.deletedAt) : comment.createdAt,
                            Etag: '', // Not available in new format
                            Size: 0, // Size not available in new format
                            StorageClass: 'STANDARD',
                            isOwner: comment.createdBy?.gid === currentUser.gid,
                            Metadata: {
                                creator: comment.createdBy?.gid || ''
                            }
                        })) as StateAttachment[];
                    } else {
                        comment.attachments = [];
                    }
                });
                return paginatedComments;
            })
        );
    }

    /**
     *
     * @param ctx State context
     * @param action Action containing optional comment, team, assignee, and scope
     * @returns Observable<void> or undefined
     *
     * This method handles transitioning a request from UNDER_REVIEW to the UNDER_REVIEW or SUBMITTED state
     * It can be triggered by assigning a request, sending it back to CRIL, or reassigning it.
     * Depending on the action parameters, it may also create a comment and update the team or assignee.
     */
    @Action([Requests.assignRequest, Requests.sendRequestBackToCril, Requests.reassign])
    public transitionToUnderReview(
        ctx: StateContext<RequestsStateModel>,
        action: Requests.sendRequestBackToCril | Requests.reassign | Requests.assignRequest
    ): Observable<void> | undefined {
        const { comment, team, assignee, scope } = action as Requests.sendRequestBackToCril & Requests.reassign;
        const { selectedRequest } = ctx.getState();
        const requestInfo = getRequestInformation<ICommonRequest>(selectedRequest, scope);

        const id = requestInfo?.id;
        const status = requestInfo?.status as RequestState;

        if (!id) {
            console.error('No request ID found for transition to under review.');
            return;
        }
        const newState = team && !assignee ? RequestState.SUBMITTED : RequestState.UNDER_REVIEW;
        const data = {} as { assignToTeam?: string; team?: string; assignTo?: string; comment?: PartialComment };
        if (team) {
            data.assignToTeam = team;
        }
        if (assignee) {
            data.assignTo = assignee;
        }
        ctx.patchState({ loading: true });

        const createComment = (
            comment
                ? ctx.dispatch(
                      new Requests.createComment(this.stateScope, {
                          requestId: id,
                          internal: false,
                          ...comment
                      })
                  )
                : of(undefined)
        ).pipe(
            catchError((error) => {
                throw error;
            })
        );

        const transition = this.requestService.transitionRequest(id, status as RequestState, newState, data).pipe(
            catchError((error) => {
                throw error;
            })
        );

        const requestUpdated = (request: ICommonRequest): boolean => {
            if (request.status !== status) {
                return true;
            }
            if (request.teamId !== requestInfo?.teamId) {
                return true;
            }
            if (request.assignee !== requestInfo?.assignee) {
                return true;
            }
            return false;
        };

        const fetchUpdated = this.requestService.getRequestById(id).pipe(
            catchError((error) => {
                throw error;
            }),
            repeat({
                delay: 500
            }),
            filter((updated) => requestUpdated(updated)),
            take(1),
            tap((updated) => {
                ctx.patchState({
                    selectedRequest: {
                        ...ctx.getState().selectedRequest,
                        requestInformation: updated,
                        request: updated
                    },
                    loading: false
                });
            })
        );

        const fetchNewTransitionChoices = (): Observable<void> =>
            ctx.dispatch(new Requests.getTransitionChoices(this.stateScope, id));

        return createComment.pipe(
            take(1),
            concatMap(() => transition),
            concatMap(() => fetchUpdated),
            concatMap(() => fetchNewTransitionChoices()),
            finalize(() =>
                ctx.setState(
                    patch<RequestsStateModel>({
                        loading: false
                    })
                )
            )
        );
    }

    /**
     * Common method for closing requests. Each substate should implement their own closeRequest action
     * and call this method with the appropriate updateFn for classification handling.
     * @param ctx State context
     * @param comment Comment to add when closing
     * @param id Request ID
     * @param status Current request status
     * @param scope MDLA scope
     * @param updateFn Function to handle classification update (can be a no-op for substates that don't need it)
     * @returns Observable<void>
     */
    protected closeRequestCommon(
        ctx: StateContext<RequestsStateModel>,
        comment: PartialComment,
        id: string,
        status: string,
        updateFn: () => Observable<void>,
        transitionFn?: () => Observable<void>
    ): Observable<void> {
        ctx.patchState({ loading: true });

        const createComment = ctx
            .dispatch(
                new Requests.createComment(this.stateScope, {
                    requestId: id,
                    internal: false,
                    ...comment
                })
            )
            .pipe(
                catchError((error) => {
                    throw error;
                })
            );

        const transition =
            transitionFn !== undefined
                ? transitionFn()
                : this.requestService
                      .transitionRequest(id, status as RequestState, RequestState.CLOSED, {
                          comment
                      })
                      .pipe(
                          catchError((error) => {
                              throw error;
                          })
                      );

        const fetchUpdated = this.requestService.getRequestById(id).pipe(
            catchError((error) => {
                throw error;
            }),
            repeat({
                delay: 500
            }),
            filter((updated) => updated.status !== status),
            take(1),
            tap((updated) => {
                ctx.patchState({
                    selectedRequest: {
                        ...ctx.getState().selectedRequest,
                        requestInformation: updated,
                        request: updated
                    }
                });
            }),
            concatMap(() => ctx.dispatch(new Requests.loadRequestAttachments(this.stateScope, id))),
            concatMap(() => ctx.dispatch(new Requests.loadRequestComments(this.stateScope, id))),
            delay(500),
            concatMap(() => ctx.dispatch(new Requests.loadRequestActivityStream(this.stateScope, id)))
        );

        const fetchNewTransitionChoices = (): Observable<void> =>
            ctx.dispatch(new Requests.getTransitionChoices(this.stateScope, id));

        return createComment.pipe(
            take(1),
            concatMap(() => updateFn()),
            concatMap(() => transition),
            concatMap(() => fetchUpdated),
            concatMap(() => fetchNewTransitionChoices()),
            finalize(() => {
                ctx.setState(
                    patch<RequestsStateModel>({
                        loading: false
                    })
                );
            })
        );
    }
}
